import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import { formatTimestamp } from '@/lib/resumeTemplateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, ChevronRight } from 'lucide-react';

export function VersionHistorySheet({ activeVersionId }) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['v1-resume-template-versions'],
    queryFn: () => v1Get('/resume-templates/versions?page=1&page_size=50'),
    enabled: open,
    staleTime: 30_000,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['v1-resume-template-version', selectedId],
    queryFn: () => v1Get(`/resume-templates/versions/${selectedId}`),
    enabled: !!selectedId,
  });

  const versions = pageData?.items || [];

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setSelectedId(null);
      }}
    >
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="resume-template-version-history-button">
          <History className="h-4 w-4" /> Version history
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full max-w-[640px] overflow-y-auto sm:max-w-[640px]"
        data-testid="resume-template-version-history-sheet"
      >
        <SheetHeader>
          <SheetTitle>
            {selectedId ? `Version v${detail?.version ?? '…'} (read-only)` : 'Version history'}
          </SheetTitle>
          <SheetDescription>
            {selectedId
              ? 'Read-only snapshot of this historical version. To restore, copy the sections manually and save a new version.'
              : 'Every save creates a new immutable version. Newest first.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {!selectedId && (
            <VersionList
              versions={versions}
              activeVersionId={activeVersionId}
              isLoading={isLoading}
              onSelect={(id) => setSelectedId(id)}
            />
          )}
          {selectedId && (
            <VersionDetail
              detail={detail}
              loading={detailLoading}
              onBack={() => setSelectedId(null)}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function VersionList({ versions, activeVersionId, isLoading, onSelect }) {
  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="resume-template-version-list-loading">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }
  if (!versions.length) {
    return (
      <div className="rounded-xl border bg-secondary/30 p-6 text-center text-sm text-muted-foreground" data-testid="resume-template-version-list-empty">
        No versions yet. Save the template once to create v1.
      </div>
    );
  }
  return (
    <ScrollArea className="h-[calc(100vh-180px)] pr-2">
      <ul className="space-y-2" data-testid="resume-template-version-list">
        {versions.map((v) => {
          const isActive = v.version_id === activeVersionId || v.is_active;
          return (
            <li key={v.version_id}>
              <button
                type="button"
                onClick={() => onSelect(v.version_id)}
                className="group flex w-full items-center gap-3 rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`resume-template-version-row-${v.version}`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 font-semibold text-primary">
                  v{v.version}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">Version {v.version}</p>
                    {isActive && (
                      <Badge variant="secondary" className="border border-primary/20 bg-primary/10 px-1.5 py-0 text-[10px] text-primary">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatTimestamp(v.created_at)} · {v.sections_count} section{v.sections_count === 1 ? '' : 's'}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </button>
            </li>
          );
        })}
      </ul>
    </ScrollArea>
  );
}

function VersionDetail({ detail, loading, onBack }) {
  if (loading || !detail) {
    return (
      <div className="space-y-3" data-testid="resume-template-version-detail-loading">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="resume-template-version-detail">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="resume-template-version-detail-back">
        ← Back to list
      </Button>

      <div className="rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono">v{detail.version}</Badge>
          {detail.is_active && (
            <Badge variant="secondary" className="border border-primary/20 bg-primary/10 text-primary">Active</Badge>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{formatTimestamp(detail.created_at)}</span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="text-muted-foreground">Brand colour</p>
            <p className="inline-flex items-center gap-2 font-mono">
              <span
                className="inline-block h-3 w-3 rounded-sm border"
                style={{ background: detail.brand_color || 'transparent' }}
                aria-hidden="true"
              />
              {detail.brand_color || '—'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Show photo</p>
            <p className="font-mono">{detail.show_photo ? 'true' : 'false'}</p>
          </div>
        </div>
        {detail.footer_note && (
          <div className="mt-3 text-xs">
            <p className="text-muted-foreground">Footer note</p>
            <p className="whitespace-pre-wrap rounded-md bg-secondary/40 p-2 font-mono">{detail.footer_note}</p>
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold">Sections</p>
        <ul className="space-y-2">
          {(detail.sections || []).sort((a, b) => a.order - b.order).map((s) => (
            <li
              key={s.key}
              className={`rounded-lg border bg-card p-3 ${s.enabled ? 'border-l-4 border-l-primary/60' : 'border-l-4 border-l-muted opacity-70'}`}
              data-testid={`resume-template-version-detail-section-${s.key}`}
            >
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono text-[10px]">{s.key}</Badge>
                <span className="text-sm font-medium">{s.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  Order {s.order} · {s.enabled ? 'Enabled' : 'Hidden'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ResumeTemplateSkeleton() {
  return (
    <div className="space-y-6" data-testid="resume-template-loading">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-36" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,_460px)_minmax(0,_1fr)]">
        <Skeleton className="h-[520px] w-full rounded-xl" />
        <Skeleton className="h-[520px] w-full rounded-xl" />
      </div>
    </div>
  );
}
